// src/prototype/utils/exportReturn.ts
// ============================================================================
// EXPORT ROUND-TRIP — one definition of how a use-case export carries its
// identity out and finds its way back.
//
// THE BUG THIS FIXES. Both Export buttons opened `/use-case/print` in a NEW TAB
// with no id and no return path. A new tab has no router history and no router
// state, so:
//   - the print page could not know which use case it was exporting (it fell
//     back to rank 1, whatever you had open), and
//   - its back button hardcoded `/opportunities`, whose tab state lived in
//     `location.state.returnToTab` — state that cannot survive window.open — so
//     it always defaulted to Portfolio Strategy, never the Explorer you left.
//
// Everything the round trip needs therefore has to travel in the URL. It does
// now, through these two builders and one reader, so the two entry points, the
// print page and the Explorer cannot disagree about the contract.
//
// WHY THE EXPLORER TAB BECAME ADDRESSABLE. The fix could have been a bespoke
// flag read only by this one path. Instead `/opportunities` now accepts
// `?tab=` and `?focus=` as ordinary route state, because a tab you can only
// reach by clicking is a tab nothing can link to — not this export, not a
// bookmark, not the next feature that needs it. Router state still works for
// in-app navigations; the URL is simply the form that survives a new tab.
// ============================================================================

/** Evaluate's two tabs, as they appear in the URL. */
export type EvaluateTabParam = 'report' | 'explore' | 'matrix';

export const EXPLORER_ROUTE = '/opportunities';
export const USE_CASE_EXPORT_ROUTE = '/use-case/print';

/** Query keys, named once so a typo cannot silently disable the round trip. */
export const PARAM = {
  tab: 'tab',
  focus: 'focus',
  id: 'id',
  returnTo: 'returnTo',
} as const;

/**
 * Where an export should return to: the Explorer, on the Use Case Explorer tab,
 * with the exported use case focused.
 */
export function explorerReturnPath(useCaseId?: string): string {
  const params = new URLSearchParams({ [PARAM.tab]: 'explore' });
  if (useCaseId) params.set(PARAM.focus, useCaseId);
  return `${EXPLORER_ROUTE}?${params.toString()}`;
}

/**
 * The export URL for a use case, carrying both its identity and its way home.
 * Used by every entry point into the export view.
 */
export function useCaseExportUrl(useCaseId?: string): string {
  const params = new URLSearchParams();
  if (useCaseId) params.set(PARAM.id, useCaseId);
  params.set(PARAM.returnTo, explorerReturnPath(useCaseId));
  return `${USE_CASE_EXPORT_ROUTE}?${params.toString()}`;
}

/**
 * A same-origin absolute path, or null. Guards the one parameter a viewer could
 * hand-edit: `returnTo` drives a navigate(), so anything that is not a rooted
 * in-app path (protocol-relative `//evil.example` included) is refused rather
 * than followed.
 */
export function safeInternalPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

/**
 * Resolve where the export's back button goes, from its own query string.
 * Prefers an explicit `returnTo`; falls back to deriving one from `id` so a
 * hand-typed `/use-case/print?id=opp-4` still returns somewhere useful; and
 * finally to the Explorer with nothing focused.
 */
export function resolveExportReturn(search: string): string {
  const params = new URLSearchParams(search);
  return (
    safeInternalPath(params.get(PARAM.returnTo)) ??
    explorerReturnPath(params.get(PARAM.id) ?? undefined)
  );
}

/** The use case an export is for, from its own query string. */
export function readExportedUseCaseId(search: string): string | null {
  return new URLSearchParams(search).get(PARAM.id);
}

/** Read `?tab=` if it names a real tab. */
export function readTabParam(search: string): EvaluateTabParam | null {
  const value = new URLSearchParams(search).get(PARAM.tab);
  return value === 'report' || value === 'explore' || value === 'matrix' ? value : null;
}

/** Read `?focus=`. */
export function readFocusParam(search: string): string | null {
  return new URLSearchParams(search).get(PARAM.focus);
}
